import { useEffect, useState, useCallback } from 'react';
import { useVacancyStore } from '../store/vacancyStore';
import { optimizedApiService } from '../services/optimizedApiService';
import { useRoleAccess } from './useRoleAccess';
import type { Position as ApiPosition } from '../../generated-src/client/positions/api';

const MAX_VACANCIES_FOR_SIDEBAR = 1000;

export function useVacanciesData() {
  const { isRecruiter } = useRoleAccess();
  const {
    vacancies,
    filterTab,
    selectedStatusFilter,
    searchValue,
    setVacancies,
    setTotalPages,
    setSelectedVacancyById,
    selectedVacancyId,
    selectedVacancy,
  } = useVacancyStore();

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Загрузка вакансий
  const loadVacancies = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const effectiveFilterTab = isRecruiter ? 'my' : filterTab;
      
      // Всегда загружаем все вакансии для бокового меню (все страницы)
      let allPositions: ApiPosition[] = [];
      let page = 0;
      let total = 0;
      let hasMore = true;

      while (hasMore) {
        const data = await optimizedApiService.getVacanciesPageData({
          search: searchValue || undefined,
          page: page,
          size: MAX_VACANCIES_FOR_SIDEBAR,
          owner: effectiveFilterTab === 'my' ? 'me' : 'all',
          status: selectedStatusFilter === 'all' ? undefined : selectedStatusFilter,
        });

        allPositions = [...allPositions, ...data.positions];
        total = data.total;
        
        // Если загрузили все или получили меньше чем запросили, значит это последняя страница
        hasMore = data.positions.length === MAX_VACANCIES_FOR_SIDEBAR && allPositions.length < total;
        page++;
        
        // Защита от бесконечного цикла
        if (page > 100) {
          console.warn('useVacanciesData: Too many pages, stopping at page', page);
          break;
        }
      }

      console.log('useVacanciesData: Loaded all vacancies:', allPositions.length, 'total:', total);

      setVacancies(allPositions);
      setTotalPages(1);

      // Автовыбор первой вакансии только при первой загрузке и если нет выбранной
      const currentVacancyState = useVacancyStore.getState();
      if (currentVacancyState.isFirstLoad && !currentVacancyState.selectedVacancyId && allPositions.length > 0) {
        console.log('useVacanciesData: Auto-selecting first vacancy:', allPositions[0].title);
        setSelectedVacancyById(allPositions[0].id);
        // После первой загрузки больше не автовыбираем
        useVacancyStore.setState({ isFirstLoad: false });
      }
    } catch (err) {
      console.error('useVacanciesData: Error loading vacancies:', err);
      setError(err instanceof Error ? err.message : 'Failed to load vacancies');
    } finally {
      setIsLoading(false);
    }
  }, [
    searchValue,
    filterTab,
    selectedStatusFilter,
    setVacancies,
    setTotalPages,
    setSelectedVacancyById,
    isRecruiter,
  ]);

  // Загружаем вакансии при монтировании и при изменении фильтров
  useEffect(() => {
    loadVacancies();
  }, [loadVacancies]);

  // Загружаем выбранную вакансию, если её нет в списке
  useEffect(() => {
    console.log('useVacanciesData useEffect: selectedVacancyId:', selectedVacancyId, 'selectedVacancy:', selectedVacancy?.title);

    // Догрузка должна идти через vacancyStore: там есть request sequence guard и очистка 403/404.
    if (selectedVacancyId && !selectedVacancy) {
      setSelectedVacancyById(selectedVacancyId);
    } else {
      console.log('useVacanciesData: Skipping load - either no ID or vacancy already loaded');
    }
  }, [selectedVacancyId, selectedVacancy, setSelectedVacancyById]);

  return {
    vacancies,
    isLoading,
    error,
    reload: loadVacancies,
  };
}
